/**
 * EventIssueCell — inline cell in the /events table that either:
 *   1. links to an existing GitHub issue (when `event.githubIssueNumber` is
 *      set), or
 *   2. exposes a small "Issue erstellen" icon-button that calls the per-type
 *      create endpoint without forcing the user to expand the row first.
 *
 * Visibility-as-duplicate-shield (#638) + direct-action (#637) — the data
 * (`githubIssueUrl` / `githubIssueNumber`) is already on the events-list
 * summary, so this is FE-only.
 *
 * The expanded detail panel keeps its own (larger) CreateIssueButton — that
 * UX context still benefits from a labelled button next to the description.
 */

import React, { useState } from 'react';
import {
  Alert,
  CircularProgress,
  IconButton,
  Link,
  Snackbar,
  Stack,
  Tooltip,
} from '@mui/material';
import { AddCircleOutline, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { eventsApi } from '../../services/eventsApi';
import type { CreateIssueResponse, PortalEvent } from '../../services/types';

interface Props {
  event: PortalEvent;
  /** Optional refresh hook so the parent can flip the cell to "link" mode. */
  onIssueCreated?: (eventId: string, response: CreateIssueResponse) => void;
}

type SnackbarState =
  | { kind: 'success'; message: string; url: string }
  | { kind: 'info'; message: string; url: string }
  | { kind: 'error'; message: string }
  | null;

const EventIssueCell: React.FC<Props> = ({ event, onIssueCreated }) => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>(null);
  const closeSnackbar = () => setSnackbar(null);

  if (event.githubIssueUrl) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Link
          href={event.githubIssueUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          onClick={(e) => e.stopPropagation()}
          aria-label={`GitHub-Issue #${event.githubIssueNumber} oeffnen`}
        >
          #{event.githubIssueNumber}
        </Link>
        <OpenInNewIcon fontSize="inherit" color="action" />
      </Stack>
    );
  }

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const result = await (event.type === 'feedback'
        ? eventsApi.createFeedbackIssue(event.id)
        : eventsApi.createErrorIssue(event.id));

      setSnackbar({
        kind: result.alreadyExisted ? 'info' : 'success',
        message: result.alreadyExisted
          ? `Issue #${result.number} wurde bereits erstellt.`
          : `Issue #${result.number} erstellt.`,
        url: result.url,
      });
      onIssueCreated?.(event.id, result);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Issue-Service nicht erreichbar.';
      setSnackbar({ kind: 'error', message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="GitHub-Issue erstellen">
        <span>
          <IconButton
            size="small"
            onClick={handleClick}
            disabled={loading}
            aria-label="GitHub-Issue erstellen"
          >
            {loading ? <CircularProgress size={18} /> : <AddCircleOutline fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.kind}
            onClose={closeSnackbar}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
            {(snackbar.kind === 'success' || snackbar.kind === 'info') && (
              <>
                {' — '}
                <Link
                  href={snackbar.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                  underline="always"
                >
                  oeffnen
                </Link>
              </>
            )}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </>
  );
};

export default EventIssueCell;
